import { useRef, useState, ChangeEvent } from "react";
import { useDebouncedValue } from "@tellescope/ui";
import {
  TellescopeModelFromName,
  useTellescopeInfiniteQuery,
  useUploadTellescopeFile,
} from "@tellescope/tanstack-query";
import { formatFileSize } from "@tellescope/utils";
import { useTranslation } from "react-i18next";

export interface UseFilesVariantArgs {
  enduserId?: string;
  channelType: "sms" | "chat" | "email";
  selectedSecureNames: string[];
  dialogOpen: boolean;
  onAttachExisting: (files: TellescopeModelFromName<"files">[]) => void;
  onUploadNew: (files: TellescopeModelFromName<"files">[]) => void;
}

export const useFilesVariant = ({
  enduserId,
  channelType,
  selectedSecureNames,
  dialogOpen,
  onAttachExisting,
  onUploadNew,
}: UseFilesVariantArgs) => {
  const { t } = useTranslation("inbox");
  const [activeTab, setActiveTab] = useState<"upload" | "existing">("upload");
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [selectedExistingFiles, setSelectedExistingFiles] = useState<Set<string>>(new Set(selectedSecureNames));
  const [fileSearch, setFileSearch] = useState("");
  const [debouncedFileSearch] = useDebouncedValue(fileSearch);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const existingFilesQuery = useTellescopeInfiniteQuery({
    model: "files",
    method: "getSome",
    request: {
      search: debouncedFileSearch ? { query: debouncedFileSearch } : undefined,
      limit: 50,
    },
    enabled: !!enduserId && dialogOpen,
    select: (data) => data.pages.flat(),
    placeholderData: (prev) => prev,
  });

  const maxFileSizeMB = channelType === "sms" ? 5 : 1024;
  const maxFileSizeBytes = maxFileSizeMB * 1024 * 1024;
  const acceptedFileTypes = channelType === "sms" ? "image/*" : undefined;

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);

    const validFiles: File[] = [];
    const invalidFiles: string[] = [];

    files.forEach((file) => {
      if (file.size > maxFileSizeBytes) {
        invalidFiles.push(`${file.name} (${formatFileSize(file.size)})`);
      } else if (channelType === "sms" && !file.type.startsWith("image/")) {
        invalidFiles.push(`${file.name} (not an image)`);
      } else {
        validFiles.push(file);
      }
    });

    if (invalidFiles.length > 0) {
      alert(
        `${t("The following files exceed the size limit or are invalid")} (${maxFileSizeMB}MB):\n${invalidFiles.join(
          "\n"
        )}`
      );
    }

    setSelectedFiles((prev) => [...prev, ...validFiles]);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const toggleExistingFile = (secureName: string) => {
    setSelectedExistingFiles((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(secureName)) {
        newSet.delete(secureName);
      } else {
        newSet.add(secureName);
      }
      return newSet;
    });
  };

  const clearExistingSelection = () => setSelectedExistingFiles(new Set());

  const changeTab = (tab: "upload" | "existing") => {
    if (tab === "upload") {
      setSelectedExistingFiles(new Set());
    }
    setActiveTab(tab);
  };

  const { uploadFileAsync } = useUploadTellescopeFile();

  const attach = async () => {
    const existingFiles = existingFilesQuery.data?.filter((file) => selectedExistingFiles.has(file.secureName)) ?? [];

    if (selectedFiles.length > 0) {
      const res = await Promise.allSettled(
        selectedFiles.map((file) =>
          uploadFileAsync({ enduserId, name: file.name, size: file.size, type: file.type }, file)
        )
      );

      const successfulFiles = res.reduce<TellescopeModelFromName<"files">[]>((acc, result) => {
        if (result.status === "fulfilled") {
          acc.push(result.value);
        }
        return acc;
      }, []);
      onUploadNew(successfulFiles);
    }

    if (existingFiles.length > 0) {
      onAttachExisting(existingFiles);
    }
  };

  const reset = () => {
    setSelectedFiles([]);
    setSelectedExistingFiles(new Set());
    setActiveTab("upload");
    setFileSearch("");
  };

  const totalSelectedCount = selectedFiles.length + selectedExistingFiles.size;

  return {
    activeTab,
    changeTab,
    selectedFiles,
    selectedExistingFiles,
    fileSearch,
    setFileSearch,
    debouncedFileSearch,
    fileInputRef,
    existingFilesQuery,
    maxFileSizeMB,
    acceptedFileTypes,
    channelType,
    handleFileChange,
    handleRemoveFile,
    toggleExistingFile,
    clearExistingSelection,
    attach,
    reset,
    totalSelectedCount,
  };
};
